import React, { FC, useCallback, useEffect, useState } from 'react';

import { CheckType } from '@ariksa/compliance-policies/api';
import { NativeResources } from '@ariksa/inventory-core';
import {
  AggregatedAlertDetails,
  Clients,
  ReportFor,
} from '@ariksa/notification';
import { AlertResponse, NotificationFor } from '@ariksa/notification/api';
import { Box, HStack, Stack, useDisclosure } from '@chakra-ui/react';
import { filter, isEmpty, isEqual } from 'lodash';
import map from 'lodash/map';
import { useDispatch, useSelector } from 'react-redux';
import { useAlertDownloader } from 'services/Notification/useAlertDownloader';
import { INIT_PAGE_INFO } from 'services/utils/constants';

import { isAlertVaultEnabled } from 'api/alert-vault/client';
import {
  DrawerAlertCounts,
  StatusMap,
} from 'components/DataDisplay/Misc/DrawerAlertCounts';
import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import { Sorted } from 'components/DataDisplay/NewTable/types';
import {
  DetailsIdentifier,
  DownloadIconButton,
  ExceptionIcon,
  NotifyIcon,
  PageInfo,
  RemediateIcon,
  SeverityIcon,
  TableActionsMenu,
  TicketIcon,
} from 'components/index';
import {
  selectActiveCloudAccount,
  selectActiveEnvironment,
} from 'containers/App/selectors';
import { EnvironmentName } from 'containers/App/utils';
import { AlertDetailsDrawer } from 'containers/Findings/AlertDrawer/Components/Aggregated/AlertDetailsDrawer';
import { useFindingsOverviewColumns } from 'containers/Findings/AlertDrawer/Components/Aggregated/hooks/useFindingsOverviewColumns';
import { RemediationModal } from 'containers/Findings/AlertDrawer/Components/Individual/Remediation/RemediationModal';
import { useAlertStatus } from 'containers/Findings/AlertDrawer/hooks/useAlertStatus';
import { selectAlertDrawer } from 'containers/Findings/AlertDrawer/selectors';
import { AddException } from 'containers/Findings/Alerts/Components/AddException';
import { CreateNotification } from 'containers/Findings/Alerts/Components/CreateNotification';
import { CreateTicket } from 'containers/Findings/Alerts/Components/CreateTicket';
import { selectFindingsOverview } from 'containers/Findings/FindingsOverview/selectors';
import { actions as findingsOverviewActions } from 'containers/Findings/FindingsOverview/slice';
import { useSearchParams } from 'hooks/useSearchParams';

interface IAlertTableByRuleId {
  aggregatedAlert: AggregatedAlertDetails;
  showSummary?: boolean;
}

export const IndividualAlertTable: FC<IAlertTableByRuleId> = props => {
  const dispatch = useDispatch();
  const { aggregatedAlert, showSummary } = props;
  const { alertsByRuleId, alertFilters } = useSelector(selectFindingsOverview);
  const { environmentId: environment_id } = useSearchParams<{
    environmentId: string;
  }>();

  const { alertListStatus } = useSelector(selectAlertDrawer);
  const { accountId } = useSelector(selectActiveCloudAccount);
  const { environmentId } = useSelector(selectActiveEnvironment);

  const exceptionModal = useDisclosure();
  const ticketModal = useDisclosure();
  const notificationModal = useDisclosure();
  const remediationModalState = useDisclosure();
  const alertDetails = useDisclosure();
  const { columns } = useFindingsOverviewColumns({
    aggregatedAlert: aggregatedAlert,
  });

  const [currentRow, setCurrentRow] = useState<AlertResponse | null>(null);
  const [changeSeverity, setChangeSeverity] = useState(false);
  const [statusData, setStatusData] = useState<StatusMap>({
    critical: 0,
    medium: 0,
    low: 0,
  });
  const { getAlertListStatus } = useAlertStatus();

  const [sortBy, setSortBy] = useState<Sorted>({
    sortField: 'entity_name',
    sortOrder: 'asc',
  });

  const handleSort = (sortInfo: Sorted) => {
    if (!isEqual(sortBy, sortInfo)) {
      setSortBy(sortInfo);
    }
  };

  const getAlertRemediationAvailability = useCallback(() => {
    if (!aggregatedAlert?.alert_rule_ids?.length) return;
    dispatch(
      findingsOverviewActions.getAlertByRuleIdRemediationAvailability({
        q: {
          checkDetailsInfo: {
            check_type: CheckType.Instances,
            check_names: aggregatedAlert?.alert_rule_ids ?? [],
          },
        },
      }),
    );
  }, [aggregatedAlert?.alert_rule_ids, dispatch]);

  const getResourceVPCs = useCallback(
    (aggregatedAlert, alertsByRuleId) => {
      if (!aggregatedAlert?.resource_types?.length) return;
      dispatch(
        findingsOverviewActions.getResourceVPC({
          q: {
            insightV2Request: {
              resource_type: aggregatedAlert
                ?.resource_types[0] as NativeResources,
              account_id: accountId ? [accountId] : [],
              environment_id: environment_id ?? environmentId,
              resource_uuids: alertsByRuleId.results.map(a => a.entity_uuid),
            },
          },
          page: { page_size: 100, page_number: 1 },
        }),
      );
    },
    [accountId, dispatch, environmentId, environment_id],
  );

  const loadAlertsByRuleId = useCallback(
    (page: PageInfo = INIT_PAGE_INFO) => {
      if (!aggregatedAlert) return;

      const filter = {};
      if (alertFilters.severity) {
        filter['severity'] = alertFilters.severity;
      }

      dispatch(
        findingsOverviewActions.getAlertsByRuleId({
          q: {
            accountId,
            environmentId: environment_id ?? environmentId,
            groupId: aggregatedAlert?.group_id,
            alertCategory: aggregatedAlert.alert_category,
            ...sortBy,
            ...filter,
          },
          page,
          onSuccess: res => {
            getAlertRemediationAvailability();
            getResourceVPCs(aggregatedAlert, res);
            dispatch(
              findingsOverviewActions.getAlertWorkflowStatus({
                q: { alertId: map(res?.results, o => o?.uuid ?? '') },
              }),
            );

            dispatch(
              findingsOverviewActions.getAlertLogs({
                q: { alertId: map(res?.results, o => o?.uuid ?? '') },
              }),
            );

            dispatch(
              findingsOverviewActions.getRiskContext({
                q: {
                  riskContextRequest: {
                    uuids: map(res?.results, o => o?.entity_uuid ?? '') ?? [],
                  },
                },
              }),
            );

            dispatch(
              findingsOverviewActions.getCompliance({
                q: {
                  getRuleComplianceStandardRequest: {
                    rules: aggregatedAlert.alert_rule_ids ?? [],
                  },
                },
              }),
            );

            getAlertListStatus(map(res?.results, o => o?.uuid ?? '') ?? []);
          },
        }),
      );
    },
    [
      aggregatedAlert,
      alertFilters.severity,
      dispatch,
      accountId,
      environmentId,
      environment_id,
      sortBy,
      getAlertRemediationAvailability,
      getResourceVPCs,
      getAlertListStatus,
    ],
  );

  useEffect(() => {
    loadAlertsByRuleId();
  }, [loadAlertsByRuleId]);

  const renderActions = useCallback(
    ({ row }) => {
      const tickets = filter(
        alertListStatus.data?.[row?.uuid],
        o => o.action === Clients.Jira || o.action === Clients.Servicenow,
      );
      const menuItems = [
        {
          label: 'Create ticket',
          isDisabled: !isEmpty(tickets),
          icon: <TicketIcon />,
          onClick: e => {
            e.stopPropagation();
            setCurrentRow(row);
            ticketModal.onOpen();
          },
        },
        {
          label: 'Send notification',
          icon: <NotifyIcon />,
          onClick: e => {
            e.stopPropagation();
            setCurrentRow(row);
            notificationModal.onOpen();
          },
        },
        {
          label: 'Add exception',
          icon: <ExceptionIcon />,
          onClick: e => {
            e.stopPropagation();
            setCurrentRow(row);
            exceptionModal.onOpen();
          },
        },
        {
          label: 'Remediate',
          icon: <RemediateIcon />,
          onClick: e => {
            e.stopPropagation();
            setCurrentRow(row);
            remediationModalState.onOpen();
          },
        },
        {
          label: 'Change severity',
          icon: <SeverityIcon />,
          onClick: e => {
            e.stopPropagation();
            setCurrentRow(row);
            setChangeSeverity(true);
            exceptionModal.onOpen();
          },
        },
      ];

      return (
        <Box onClick={e => e.stopPropagation()}>
          <TableActionsMenu menuItems={menuItems} styles={{ item: { h: 8 } }} />
        </Box>
      );
    },
    [
      exceptionModal,
      notificationModal,
      remediationModalState,
      ticketModal,
      alertListStatus.data,
    ],
  );

  //calculate status data
  useEffect(() => {
    let status = {
      critical: aggregatedAlert?.severity?.CRITICAL,
      high: aggregatedAlert?.severity?.HIGH,
      medium: aggregatedAlert?.severity?.MEDIUM,
      low: aggregatedAlert?.severity?.LOW,
    };

    setStatusData(status);
  }, [aggregatedAlert]);
  const { downloadReport } = useAlertDownloader();

  return (
    <Stack h={'full'} spacing={5} id={'individual-alert-table-drawer'}>
      {showSummary && (
        <HStack align={'flex-start'} justify={'space-between'}>
          <Stack spacing={4}>
            <DetailsIdentifier
              label="Description"
              value={aggregatedAlert?.description ?? '-'}
            />
            <DetailsIdentifier
              label="Entity Type"
              value={aggregatedAlert?.resource_types?.join(', ') ?? '-'}
            />
            <DrawerAlertCounts statusMap={statusData} />

            <DetailsIdentifier
              label="Environment"
              value={
                <EnvironmentName
                  environmentId={environment_id ?? environmentId}
                />
              }
            />
          </Stack>
          <DownloadIconButton
            onClick={() => {
              downloadReport({
                reportType: ReportFor.IndividualAlerts,
                category: aggregatedAlert?.alert_category,
                groupId: aggregatedAlert?.group_id,
              });
            }}
            aria-label={''}
          />
        </HStack>
      )}
      <Box flex={1}>
        <Table
          data={alertsByRuleId.data}
          columns={[
            ...columns,
            {
              header: 'Actions',
              render: renderActions,
            },
          ]}
          pagination={{
            pageInfo: alertsByRuleId.page.info,
            totalCount: alertsByRuleId.page.totalCount,
            cursorBased: isAlertVaultEnabled(),
            // Alert Vault alert lists return the exact total.
            exactTotal: isAlertVaultEnabled(),
            onChange: loadAlertsByRuleId,
          }}
          isLoading={alertsByRuleId.isLoading}
          styles={{ height: '34rem' }}
          onSort={handleSort}
          sortBy={sortBy}
          headerClassName="dashboard_alerts_table"
          isRowActive={r => r.entity_uuid === currentRow?.entity_uuid}
          cursor={'pointer'}
          onRowClick={r => {
            // show alert details
            setCurrentRow(r);
            alertDetails.onOpen();
          }}
        />
      </Box>

      {currentRow && exceptionModal.isOpen && (
        <AddException
          onClose={() => {
            exceptionModal.onClose();
            setChangeSeverity(false);
          }}
          isOpen={exceptionModal.isOpen}
          alert={currentRow}
          exceptionFor={'Issue'}
          changeSeverity={changeSeverity}
        />
      )}

      {ticketModal.isOpen && !!currentRow && (
        <CreateTicket
          onClose={ticketModal.onClose}
          isOpen={ticketModal.isOpen}
          alert={currentRow}
          onCreate={() =>
            getAlertListStatus(alertsByRuleId.data.map(a => a.uuid ?? ''))
          }
          notification_for={NotificationFor.Alerts}
        />
      )}
      {notificationModal.isOpen && !!currentRow && (
        <CreateNotification
          onClose={notificationModal.onClose}
          isOpen={notificationModal.isOpen}
          alert={currentRow}
          notification_for={NotificationFor.Alerts}
        />
      )}
      {!!currentRow && remediationModalState.isOpen && (
        <RemediationModal state={remediationModalState} alert={currentRow} />
      )}
      {!!currentRow && alertDetails.isOpen && (
        <AlertDetailsDrawer
          alert={currentRow}
          isOpen={alertDetails.isOpen}
          onClose={alertDetails.onClose}
        />
      )}
    </Stack>
  );
};
